/**
 * BatchOperationsPage
 *
 * List and manage batch operations for bulk auto-enrich, tag analysis, and publishing.
 */

import { useState } from 'react';
import { Plus, Filter, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BatchOperationCard } from '@/components/batch-operations/BatchOperationCard';
import { CreateBatchDialog } from '@/components/batch-operations/CreateBatchDialog';
import {
  useBatchOperations,
  usePauseBatchOperation,
  useResumeBatchOperation,
  useCancelBatchOperation,
} from '@/api/batch-operations';
import { usePartnerAccounts } from '@/api/partner-accounts';

const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'running', label: 'Running' },
  { value: 'pending', label: 'Pending' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const OPERATION_TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'auto_enrich', label: 'Auto-Enrich' },
  { value: 'batch_tags', label: 'Tag Analysis' },
  { value: 'batch_publish', label: 'Batch Publish' },
  { value: 'bulk_update', label: 'Bulk Update' },
  { value: 'batch_classify_type', label: 'Classify type, styles & features' },
  { value: 'ai_content', label: 'AI Content' },
  { value: 'batch_resync_content', label: 'Re-sync AI Content' },
  { value: 'account_disable', label: 'Disable Account' },
];

export function BatchOperationsPage(): JSX.Element {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');

  const { data: partnerAccountsData, isLoading: loadingAccounts } = usePartnerAccounts();
  const { data: operationsData, isLoading: loadingOperations, refetch } = useBatchOperations({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    operationType: typeFilter !== 'all' ? typeFilter : undefined,
    partnerAccountId: accountFilter !== 'all' ? accountFilter : undefined,
    limit: 50,
  });

  const pauseMutation = usePauseBatchOperation();
  const resumeMutation = useResumeBatchOperation();
  const cancelMutation = useCancelBatchOperation();

  const handlePause = (id: string): void => {
    pauseMutation.mutate(id);
  };

  const handleResume = (id: string): void => {
    resumeMutation.mutate(id);
  };

  const handleCancel = (id: string): void => {
    cancelMutation.mutate(id);
  };

  const partnerAccounts = partnerAccountsData ?? [];
  const operations = operationsData?.data ?? [];

  return (
    <div className="space-y-6" data-testid="batch-operations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Batch Operations</h1>
          <p className="text-muted-foreground">
            Manage bulk auto-enrich, tag analysis, and publishing operations
          </p>
        </div>
        <Button onClick={() => { setShowCreateDialog(true); }} data-testid="new-batch-operation-btn">
          <Plus className="mr-2 h-4 w-4" />
          New Batch Operation
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter:</span>
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="batch-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((filter) => (
              <SelectItem key={filter.value} value={filter.value}>
                {filter.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px]" data-testid="batch-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            {OPERATION_TYPE_FILTERS.map((filter) => (
              <SelectItem key={filter.value} value={filter.value}>
                {filter.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={accountFilter} onValueChange={setAccountFilter} disabled={loadingAccounts}>
          <SelectTrigger className="w-[200px]" data-testid="batch-account-filter">
            <SelectValue placeholder="Partner Account" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Accounts</SelectItem>
            {partnerAccounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {account.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Operations List */}
      {loadingOperations ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : operations.length === 0 ? (
        <div className="text-center py-12" data-testid="batch-operations-empty">
          <p className="text-muted-foreground mb-4">No batch operations found</p>
          <Button onClick={() => { setShowCreateDialog(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Create Your First Batch Operation
          </Button>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="batch-operations-list">
          {operations.map((operation) => (
            <BatchOperationCard
              key={operation.id}
              operation={operation}
              onPause={handlePause}
              onResume={handleResume}
              onCancel={handleCancel}
              isPausing={pauseMutation.isPending && pauseMutation.variables === operation.id}
              isResuming={resumeMutation.isPending && resumeMutation.variables === operation.id}
              isCancelling={cancelMutation.isPending && cancelMutation.variables === operation.id}
            />
          ))}
        </div>
      )}

      {/* Pagination info */}
      {operationsData && operationsData.pagination.total > 0 && (
        <div className="text-sm text-muted-foreground text-center">
          Showing {String(operations.length)} of {String(operationsData.pagination.total)} operations
        </div>
      )}

      {/* Create Dialog */}
      <CreateBatchDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={() => { void refetch(); }}
      />
    </div>
  );
}
